import React from 'react'
import { useCallback, useState, useRef, useEffect, useMemo, RefObject } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { NavBar } from '../../components/layout/NavBar';
import './Machines.module.css'
import { Services } from '@/services/services';
import { Machine } from '@/services/model/machine';
import { Translates } from '@/services/repository/i18n';
import { SubmitHandler, useForm } from 'react-hook-form';
import { MachineForm, FormData, FormErrors } from './Machines.form';
import { Doc } from '@/services/model/docs';

export const MachinePage = ({ services }: { services: Services }) => {
  const navigate = useNavigate()
  const [data, setData] = useState<FormData>()
  const [docs, setDocs] = useState<Doc[]>()
  const [isAlternativeDocs, setAlternativeDocs] = useState<boolean>(false)
  const [qrId, setQRId] = useState<string>()
  const [translates, setTranslates] = useState<Translates>()
  const [message, setMessage] = useState<string>()
  const [serverErrors, setServerErrors] = useState<FormErrors<FormData>>()
  let mid = useParams().mid

  const form = useForm<FormData>({
    defaultValues: data
  })

  const loadDocForLanguage = (machineTypeId: string, lang: string) => {
    return fetch(`/api/schema/docs/for-machine-type/${machineTypeId}?lang=${lang}`).then((resp) => {
      if (resp.status != 200) {
        return []
      }
      return resp.json().then((data) => {
        return data
      })
    })
  }

  const updateDocs = async (machineTypeId: string | undefined) => {
    if (machineTypeId === undefined) {
      return
    }
    setAlternativeDocs(false)
    try {
      let docs = await loadDocForLanguage(machineTypeId, services.i18n.defaultLang())
      if (docs?.length !== 0) {
        setDocs(docs)
        return
      }
    } catch (error) {
      console.warn(error)
    }
    let docs = await loadDocForLanguage(machineTypeId, 'en')
    setAlternativeDocs(true)
    setDocs(docs)
  }

  const onSubmit = async (formData: FormData, resetForm: () => void) => {
    setMessage(undefined)
    const resp = await fetch(`/api/schema/machines/byid/${mid}/update`, {
      method: 'PUT',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    })
    if (!resp.ok) {
      setServerErrors(await resp.json())
    } else {
      setData(formData)
      setMessage('page.machine.updatedMessage')
      setTimeout(() => setMessage(''), 5000)
    }
  }

  useEffect(() => {
    services.i18n.get('main').then(setTranslates)
  }, [])

  useEffect(() => {
    services.machines.byId(mid).then((entity: Machine) => {
      setData(entity)
      form.reset(entity)
    })
  }, [mid]);

  useEffect(() => {
    services.machines.qr(mid).then((qrId: string) => {
      setQRId(qrId)
    })
  }, [mid])

  useEffect(() => {
    updateDocs(data?.machineTypeId)
  }, [data])

  if (translates === undefined || data === undefined) {
    return (<div className='spinner'></div>)
  }

  const renderQR = (qid: string) => <>
    <img className='qrimage' src={'/api/schema/qr/img/' + qid} />
    <a className='main-btn' href={'/api/schema/qr/img/' + qid} download={true}>
      {translates?.for('page.qr.download')}
    </a>
  </>

  const renderDocs = (docs: Doc[]) => {
    const renderRow = (doc: Doc) => <li>
      <a href={doc.url}>
        {doc.name}
      </a>
      <a href={doc.url} download={true}>
        ({translates?.for('page.machine.downlaod_doc')})
      </a>
    </li>
    if (docs.length === 0) {
      return <div className='empty-dock-list'>{translates?.for('page.machine.empty_dock_list')}</div>
    }
    return <ul>{docs.map((doc) => renderRow(doc))}</ul>
  }

  return (
    <div>
      <NavBar services={services} />
      <section className='machines-page content-body'>
        <div className='back-button' onClick={() => navigate(`/companies/${data.companyId}/machines`)}>
          <div className='back-ico arrow-right'></div>
          <div className='back-text'>{translates.for('page.machine.back')}</div>
        </div>
        <div className='main-form'>
          <div className='form-title'>{translates.for('page.machine.formTitle')}</div>
          <MachineForm
            form={form}
            services={services}
            onSubmit={onSubmit}
            serverErrors={serverErrors}
          />
          {message ? (<div className='form-message'>{translates.for(message)}</div>) : null}
        </div>

        <div className='main-block'>
          <h3>{translates.for('page.machine.documentations_section_title')}</h3>
          {isAlternativeDocs ? translates.for('page.machine.it_is_alternative_documentation') : null}
          {docs !== undefined ? renderDocs(docs) : null}
        </div>

        <div className='main-block'>
          <h3>{translates.for('page.machine.qr_section_title')}</h3>
          {qrId ? renderQR(qrId) : null}
        </div>
      </section>
    </div>
  )
}
